import React, { useState, useEffect } from 'react';
import { Box, Grid, IconButton, Typography, Divider } from '@mui/material';
import { TwCommoditySelector } from '../../../TwCommodityTableSelector/TwCommodityTableSelector';
import {
  CommodityTableAttributes,
  CommodityList
} from '../../../../redux/api/commodities/commodities.model';
import { getItemRows, imperialToCm, unitOptions } from '../../../../utils/commodity/commodityUtils';
import { mapCommodityTableAttributesToCommodityList } from '../../../../utils/commodity/commodityUtils';
import TwField, { TwInputTypes } from '../../../TwFields/TwFields';
import { MeasurementSystem } from '../../../../providers/User/User.model';
import CloseIcon from '@mui/icons-material/Close';
import TwSelect from '../../../TwSelects/TwSelects';
import TwCheckbox from '../../../TwCheckboxes/TwCheckboxes';
import TwMetricSystemInput from '../../../TwMetricSystemInput/TwMetricSystemInput';
import TwImperialSystemInput from '../../../TwImperialSystemInput/TwImperialSystemInput';
import { useCommoditySettings } from '../../../../hooks/useCommoditySettings';
import { useAppSelector } from '../../../../redux/hooks';
import { useAppDispatch } from '../../../../redux/hooks';
import { updateNewOrderCommodities } from '../../../../redux/features/orders/ordersSlice';
import { EntityType } from '../../../../redux/Global.model';
import { updateNewTripCommodity } from '../../../../redux/features/trip/tripSlice';

interface CommodityFormLayoutProps {
  ui_uid: string;
  commodity: CommodityList;
  measurementSystem: MeasurementSystem;
  entity: EntityType;
}

export const TwCommodityRow = ({
  ui_uid,
  commodity,
  measurementSystem,
  entity
}: CommodityFormLayoutProps): JSX.Element => {
  const { new_order_commodities = [] } = useAppSelector((state) => state.orders);
  const { new_trip_commodities = [] } = useAppSelector((state) => state.trip);
  const commoditiesToUse = entity === EntityType.ORDER ? new_order_commodities : new_trip_commodities;
  const dispatch = useAppDispatch();
  const {
    isDimensionsEnabled,
    isUnitEnabled,
    isMakeEnabled,
    isModelEnabled,
    isPermitRequirementEnabled
  } = useCommoditySettings();

  const ITEM_ROWS = getItemRows({ isDimensionsEnabled, isMakeEnabled, isModelEnabled, isUnitEnabled });

  // Local state for all editable fields
  const [localQuantity, setLocalQuantity] = useState(commodity.quantity?.toString() ?? '');
  const [localWeight, setLocalWeight] = useState(commodity.weight ?? '');
  const [localMake, setLocalMake] = useState(commodity.make ?? '');
  const [localModel, setLocalModel] = useState(commodity.model ?? '');
  const [localUnit, setLocalUnit] = useState(commodity.unit ?? '');
  const [localLength, setLocalLength] = useState(commodity.length?.toString() ?? '');
  const [localWidth, setLocalWidth] = useState(commodity.width?.toString() ?? '');
  const [localHeight, setLocalHeight] = useState(commodity.height?.toString() ?? '');
  const [localNotes, setLocalNotes] = useState(commodity.notes ?? '');
  const [localPermit, setLocalPermit] = useState(!!commodity.permit);
  // For imperial system inputs
  const [localLengthLeft, setLocalLengthLeft] = useState(commodity.length_left ?? '');
  const [localLengthRight, setLocalLengthRight] = useState(commodity.length_right ?? '');
  const [localWidthLeft, setLocalWidthLeft] = useState(commodity.width_left ?? '');
  const [localWidthRight, setLocalWidthRight] = useState(commodity.width_right ?? '');
  const [localHeightLeft, setLocalHeightLeft] = useState(commodity.height_left ?? '');
  const [localHeightRight, setLocalHeightRight] = useState(commodity.height_right ?? '');

  // Sync local state if commodity changes from outside
  useEffect(() => {
    setLocalQuantity(commodity.quantity?.toString() ?? '');
    setLocalWeight(commodity.weight ?? '');
    setLocalMake(commodity.make ?? '');
    setLocalModel(commodity.model ?? '');
    setLocalUnit(commodity.unit ?? '');
    setLocalLength(commodity.length?.toString() ?? '');
    setLocalWidth(commodity.width?.toString() ?? '');
    setLocalHeight(commodity.height?.toString() ?? '');
    setLocalNotes(commodity.notes ?? '');
    setLocalPermit(!!commodity.permit);
    setLocalLengthLeft(commodity.length_left ?? '');
    setLocalLengthRight(commodity.length_right ?? '');
    setLocalWidthLeft(commodity.width_left ?? '');
    setLocalWidthRight(commodity.width_right ?? '');
    setLocalHeightLeft(commodity.height_left ?? '');
    setLocalHeightRight(commodity.height_right ?? '');
  }, [commodity]);

  const handleCommodityDescriptionChange = (description: string): void => {
    const updatedCommodities = commoditiesToUse.map((c) =>
      c.ui_uid === ui_uid ? { ...c, name: description } : c
    );
    if (entity === EntityType.ORDER) {
      dispatch(updateNewOrderCommodities(updatedCommodities));
    } else {
      dispatch(updateNewTripCommodity(updatedCommodities));
    }
  };

  const handleCommoditySelect = (
    option: CommodityTableAttributes | undefined,
    targetId: string
  ): void => {
    if (option) {
      const mappedCommodity = mapCommodityTableAttributesToCommodityList(option, targetId, commodity.id);
      const updatedCommodities = commoditiesToUse.map((c) =>
        c.ui_uid === ui_uid ? mappedCommodity : c
      );
      if (entity === EntityType.ORDER) {
        dispatch(updateNewOrderCommodities(updatedCommodities));
      } else {
        dispatch(updateNewTripCommodity(updatedCommodities));
      }
    } else {
      const targetCommodity = commoditiesToUse.find((c) => c.ui_uid === ui_uid);
      if (targetCommodity) {
        targetCommodity.name = '';
        const updatedCommodities = commoditiesToUse.map((c) =>
          c.ui_uid === ui_uid ? { ...c, ...targetCommodity } : c
        );
        if (entity === EntityType.ORDER) {
          dispatch(updateNewOrderCommodities(updatedCommodities));
        } else {
          dispatch(updateNewTripCommodity(updatedCommodities));
        }
      }
    }
  };

  const handleRemoveCommodity = (commodityId: string): void => {
    const updatedCommodities = commoditiesToUse.filter((c) => c.ui_uid !== commodityId);
    if (entity === EntityType.ORDER) {
      dispatch(updateNewOrderCommodities(updatedCommodities));
    } else {
      dispatch(updateNewTripCommodity(updatedCommodities));
    }
  };

  return (
    <Box sx={{ p: 2, background: '#fff', borderRadius: 2, mb: 1 }}>
      {/* First line: Selector + Qty + Weight + Close Icon */}
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={7}>
          {/* Commodity Selector */}
          <TwCommoditySelector
            value={commodity.commodity_id}
            textValue={commodity.name}
            onSelect={handleCommoditySelect}
            onChange={handleCommodityDescriptionChange}
            commodityId={commodity.readable_commodity_id}
            id={ui_uid}
          />
        </Grid>
        <Grid item xs={2}>
          <Box display="flex" alignItems="center" height="100%">
            <Typography
              sx={{
                color: '#6B7280',
                fontWeight: 500,
                m: '-5px 10px 5px 5px',
                fontSize: '14px',
                lineHeight: 1.5,
                alignSelf: 'center'
              }}
            >
              Qty
            </Typography>
            <TwField
              id="quantity"
              name="quantity"
              value={localQuantity}
              onInputChange={(e): void => setLocalQuantity(e.target.value)}
              onInputBlur={(): void => {
                const parsedQuantity = localQuantity === '' ? null : Number(localQuantity);
                if (parsedQuantity !== (commodity.quantity ?? null)) {
                  const updatedCommodities = commoditiesToUse.map((c) =>
                    c.ui_uid === ui_uid ? { ...c, quantity: parsedQuantity } : c
                  );
                  if (entity === EntityType.ORDER) {
                    dispatch(updateNewOrderCommodities(updatedCommodities));
                  } else {
                    dispatch(updateNewTripCommodity(updatedCommodities));
                  }
                }
              }}
              inputType={TwInputTypes.number}
              sx={{
                '& .MuiInputBase-input': {
                  textAlign: 'center',
                  fontSize: '1rem',
                  lineHeight: 1.5
                }
              }}
            />
          </Box>
        </Grid>
        <Grid item xs={2.5}>
          {/* Weight */}
          <Box display="flex" alignItems="center" height="100%">
            <Typography
              sx={{
                color: '#6B7280',
                fontWeight: 500,
                m: '-5px 10px 5px 5px',
                fontSize: '14px',
                lineHeight: 1.5,
                alignSelf: 'center'
              }}
            >
              Weight
            </Typography>
            <TwField
              sx={{
                '& .MuiInputBase-input': {
                  textAlign: 'center',
                  lineHeight: 1.5
                }
              }}
              id="weight"
              name="weight"
              value={localWeight}
              onInputChange={(e): void => setLocalWeight(e.target.value)}
              onInputBlur={(): void => {
                const parsedWeight = localWeight === '' ? null : Number(localWeight);
                if (parsedWeight !== (commodity.weight ?? null)) {
                  const updatedCommodities = commoditiesToUse.map((c) =>
                    c.ui_uid === ui_uid ? { ...c, weight: parsedWeight } : c
                  );
                  if (entity === EntityType.ORDER) {
                    dispatch(updateNewOrderCommodities(updatedCommodities));
                  } else {
                    dispatch(updateNewTripCommodity(updatedCommodities));
                  }
                }
              }}
              inputType={TwInputTypes.number}
              endAdorment={
                MeasurementSystem.IMPERIAL === measurementSystem ? (
                  <Typography color="#9CA3AF" sx={{ fontSize: '14px' }}>
                    lb
                  </Typography>
                ) : (
                  <Typography color="#9CA3AF" sx={{ fontSize: '14px' }}>
                    kg
                  </Typography>
                )
              }
            />
          </Box>
        </Grid>
        <Grid item xs={0.5} sx={{ paddingLeft: '10px !important' }}>
          {/* Close Icon */}
          <IconButton
            onClick={(): void => handleRemoveCommodity(ui_uid)}
            sx={{ mt: '-5px' }}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Grid>
        <Divider
          sx={{
            width: '98%',
            borderColor: '#E5E7EB',
            margin: '5px 16px auto'
          }}
        />
      </Grid>

      {/* Second line: Make, Model, Unit, Length, Width, Height */}
      <Grid container spacing={2} alignItems="center" sx={{ marginTop: '0px' }}>
        {isMakeEnabled && (
          <Grid item xs={ITEM_ROWS}>
            {/* Input: Make */}
            <TwField
              id="make"
              name="make"
              label="Make"
              value={localMake}
              onInputChange={(e): void => setLocalMake(e.target.value)}
              onInputBlur={(): void => {
                if (localMake !== (commodity.make ?? '')) {
                  const updatedCommodities = commoditiesToUse.map((c) =>
                    c.ui_uid === ui_uid ? { ...c, make: localMake } : c
                  );
                  if (entity === EntityType.ORDER) {
                    dispatch(updateNewOrderCommodities(updatedCommodities));
                  } else {
                    dispatch(updateNewTripCommodity(updatedCommodities));
                  }
                }
              }}
              inputType={TwInputTypes.text}
              placeholder=""
            />
          </Grid>
        )}
        {isModelEnabled && (
          <Grid item xs={ITEM_ROWS}>
            {/* Input: Model */}
            <TwField
              id="model"
              name="model"
              label="Model"
              value={localModel}
              onInputChange={(e): void => setLocalModel(e.target.value)}
              onInputBlur={(): void => {
                if (localModel !== (commodity.model ?? '')) {
                  const updatedCommodities = commoditiesToUse.map((c) =>
                    c.ui_uid === ui_uid ? { ...c, model: localModel } : c
                  );
                  if (entity === EntityType.ORDER) {
                    dispatch(updateNewOrderCommodities(updatedCommodities));
                  } else {
                    dispatch(updateNewTripCommodity(updatedCommodities));
                  }
                }
              }}
              inputType={TwInputTypes.text}
              placeholder=""
            />
          </Grid>
        )}
        {isUnitEnabled && (
          <Grid item xs={ITEM_ROWS}>
            {/* Input: Unit */}
            <TwSelect
              sx={{
                height: '77px'
              }}
              id="unit"
              name="unit"
              label="Unit"
              value={localUnit}
              onInputChange={(e): void => {
                setLocalUnit(e.target.value);
                // Convert to CommodityUnit or null
                const found = unitOptions.find((opt) => opt.value === e.target.value);
                const unitValue = found ? found.value : null;
                if (unitValue !== (commodity.unit ?? null)) {
                  const updatedCommodities = commoditiesToUse.map((c) =>
                    c.ui_uid === ui_uid ? { ...c, unit: unitValue } : c
                  );
                  if (entity === EntityType.ORDER) {
                    dispatch(updateNewOrderCommodities(updatedCommodities));
                  } else {
                    dispatch(updateNewTripCommodity(updatedCommodities));
                  }
                }
              }}
              optionList={unitOptions}
            />
          </Grid>
        )}
        {isDimensionsEnabled && (
          <>
            <Grid
              item
              xs={ITEM_ROWS}
              sx={{
                paddingTop:
                  measurementSystem === MeasurementSystem.IMPERIAL
                    ? '12px !important'
                    : '14px !important'
              }}
            >
              {/* Input: Length */}
              {measurementSystem === MeasurementSystem.IMPERIAL ? (
                <TwImperialSystemInput
                  label="Length"
                  onChange={(e): void => {
                    setLocalLengthLeft(e.leftValue);
                    setLocalLengthRight(e.rightValue);
                    if (
                      e.leftValue !== (commodity.length_left ?? '') ||
                      e.rightValue !== (commodity.length_right ?? '')
                    ) {
                      const cm = imperialToCm(Number(e.leftValue), Number(e.rightValue));
                      const updatedCommodity = {
                        ...commodity,
                        length: isNaN(cm) ? null : Number(cm.toFixed(2)),
                        length_left: e.leftValue,
                        length_right: e.rightValue
                      };
                      const updatedCommodities = commoditiesToUse.map((c) =>
                        c.ui_uid === ui_uid ? updatedCommodity : c
                      );
                      if (entity === EntityType.ORDER) {
                        dispatch(updateNewOrderCommodities(updatedCommodities));
                      } else {
                        dispatch(updateNewTripCommodity(updatedCommodities));
                      }
                    }
                  }}
                  measurementSystem={measurementSystem}
                  leftValue={localLengthLeft}
                  rightValue={localLengthRight}
                />
              ) : (
                <TwMetricSystemInput
                  label="Length"
                  cmValue={localLength}
                  onChange={(e): void => {
                    setLocalLength(e);
                    const parsedLength = e === '' ? null : Number(e);
                    if (parsedLength !== (commodity.length ?? null)) {
                      const updatedCommodities = commoditiesToUse.map((c) =>
                        c.ui_uid === ui_uid ? { ...c, length: parsedLength } : c
                      );
                      if (entity === EntityType.ORDER) {
                        dispatch(updateNewOrderCommodities(updatedCommodities));
                      } else {
                        dispatch(updateNewTripCommodity(updatedCommodities));
                      }
                    }
                  }}
                  externalError={''}
                />
              )}
            </Grid>
            <Grid
              item
              xs={ITEM_ROWS}
              sx={{
                paddingTop:
                  measurementSystem === MeasurementSystem.IMPERIAL
                    ? '12px !important'
                    : '14px !important'
              }}
            >
              {/* Input: Width */}
              {measurementSystem === MeasurementSystem.IMPERIAL ? (
                <TwImperialSystemInput
                  label="Width"
                  onChange={(e): void => {
                    setLocalWidthLeft(e.leftValue);
                    setLocalWidthRight(e.rightValue);
                    if (
                      e.leftValue !== (commodity.width_left ?? '') ||
                      e.rightValue !== (commodity.width_right ?? '')
                    ) {
                      const cm = imperialToCm(Number(e.leftValue), Number(e.rightValue));
                      const updatedCommodity = {
                        ...commodity,
                        width: isNaN(cm) ? null : Number(cm.toFixed(2)),
                        width_left: e.leftValue,
                        width_right: e.rightValue
                      };
                      const updatedCommodities = commoditiesToUse.map((c) =>
                        c.ui_uid === ui_uid ? updatedCommodity : c
                      );
                      if (entity === EntityType.ORDER) {
                        dispatch(updateNewOrderCommodities(updatedCommodities));
                      } else {
                        dispatch(updateNewTripCommodity(updatedCommodities));
                      }
                    }
                  }}
                  measurementSystem={measurementSystem}
                  leftValue={localWidthLeft}
                  rightValue={localWidthRight}
                />
              ) : (
                <TwMetricSystemInput
                  label="Width"
                  cmValue={localWidth}
                  onChange={(e): void => {
                    setLocalWidth(e);
                    const parsedWidth = e === '' ? null : Number(e);
                    if (parsedWidth !== (commodity.width ?? null)) {
                      const updatedCommodities = commoditiesToUse.map((c) =>
                        c.ui_uid === ui_uid ? { ...c, width: parsedWidth } : c
                      );
                      if (entity === EntityType.ORDER) {
                        dispatch(updateNewOrderCommodities(updatedCommodities));
                      } else {
                        dispatch(updateNewTripCommodity(updatedCommodities));
                      }
                    }
                  }}
                  externalError={''}
                />
              )}
            </Grid>
            <Grid
              item
              xs={ITEM_ROWS}
              sx={{
                paddingTop:
                  measurementSystem === MeasurementSystem.IMPERIAL
                    ? '12px !important'
                    : '14px !important'
              }}
            >
              {/* Input: Height */}
              {measurementSystem === MeasurementSystem.IMPERIAL ? (
                <TwImperialSystemInput
                  label="Height"
                  onChange={(e): void => {
                    setLocalHeightLeft(e.leftValue);
                    setLocalHeightRight(e.rightValue);
                    if (
                      e.leftValue !== (commodity.height_left ?? '') ||
                      e.rightValue !== (commodity.height_right ?? '')
                    ) {
                      const cm = imperialToCm(Number(e.leftValue), Number(e.rightValue));
                      const updatedCommodity = {
                        ...commodity,
                        height: isNaN(cm) ? null : Number(cm.toFixed(2)),
                        height_left: e.leftValue,
                        height_right: e.rightValue
                      };
                      const updatedCommodities = commoditiesToUse.map((c) =>
                        c.ui_uid === ui_uid ? updatedCommodity : c
                      );
                      if (entity === EntityType.ORDER) {
                        dispatch(updateNewOrderCommodities(updatedCommodities));
                      } else {
                        dispatch(updateNewTripCommodity(updatedCommodities));
                      }
                    }
                  }}
                  measurementSystem={measurementSystem}
                  leftValue={localHeightLeft}
                  rightValue={localHeightRight}
                />
              ) : (
                <TwMetricSystemInput
                  label="Height"
                  cmValue={localHeight}
                  onChange={(e): void => {
                    setLocalHeight(e);
                    const parsedHeight = e === '' ? null : Number(e);
                    if (parsedHeight !== (commodity.height ?? null)) {
                      const updatedCommodities = commoditiesToUse.map((c) =>
                        c.ui_uid === ui_uid ? { ...c, height: parsedHeight } : c
                      );
                      if (entity === EntityType.ORDER) {
                        dispatch(updateNewOrderCommodities(updatedCommodities));
                      } else {
                        dispatch(updateNewTripCommodity(updatedCommodities));
                      }
                    }
                  }}
                  externalError={''}
                />
              )}
            </Grid>
          </>
        )}
      </Grid>

      {/* Third line: Notes */}
      <Grid
        container
        spacing={1}
        sx={{
          marginTop:
            !isMakeEnabled && !isModelEnabled && !isDimensionsEnabled && !isUnitEnabled
              ? '10px'
              : '0px'
        }}
      >
        <Grid item xs={12} sx={{ pt: '0px !important' }}>
          {/* Input: Notes */}
          <TwField
            id="notes"
            name="notes"
            label="Notes"
            value={localNotes}
            onInputChange={(e): void => setLocalNotes(e.target.value)}
            onInputBlur={(): void => {
              if (localNotes !== (commodity.notes ?? '')) {
                const updatedCommodities = commoditiesToUse.map((c) =>
                  c.ui_uid === ui_uid ? { ...c, notes: localNotes } : c
                );
                if (entity === EntityType.ORDER) {
                  dispatch(updateNewOrderCommodities(updatedCommodities));
                } else {
                  dispatch(updateNewTripCommodity(updatedCommodities));
                }
              }
            }}
            inputType={TwInputTypes.text}
            placeholder=""
          />
        </Grid>
      </Grid>

      {/* Fourth line: Permit required */}
      {isPermitRequirementEnabled && (
        <Grid container spacing={1}>
          <Grid item xs={12}>
            {/* Input: Permit required (checkbox) */}
            <TwCheckbox
              name="permit"
              label="Permit required"
              checked={localPermit}
              onInputChange={(e): void => {
                setLocalPermit(e.target.checked);
                if (e.target.checked !== !!commodity.permit) {
                  const updatedCommodities = commoditiesToUse.map((c) =>
                    c.ui_uid === ui_uid ? { ...c, permit: e.target.checked } : c
                  );
                  if (entity === EntityType.ORDER) {
                    dispatch(updateNewOrderCommodities(updatedCommodities));
                  } else {
                    dispatch(updateNewTripCommodity(updatedCommodities));
                  }
                }
              }}
            />
          </Grid>
        </Grid>
      )}
    </Box>
  );
};
